/**
 * KD Tooltip
 *
 * Reusable, attribute-driven tooltip. Any element can opt in with data attributes:
 *
 *   data-kd-tooltip="Body text"                 (required)
 *   data-kd-tooltip-title="Short heading"       (optional)
 *   data-kd-tooltip-placement="top|bottom|left|right"  (optional, default "top"; flips when there is no room)
 *
 * A single tooltip element is appended to <body> and positioned with fixed coordinates, so it is never clipped by
 * `overflow: auto/hidden` ancestors (e.g. the product gallery thumbnail strip). It opens on mouse hover and keyboard
 * focus, closes on Escape, click, scroll and resize, and is linked to its trigger through aria-describedby.
 * Touch input never opens it (there is no hover on touch), so it cannot get stuck open after a tap.
 *
 * Styles live in kd-tooltip.css. Load both through `{% render 'kd-tooltip-assets' %}`.
 */
(function (root) {
  "use strict";

  var SELECTOR = "[data-kd-tooltip]";
  var GAP = 12; // distance between trigger and tooltip (room for the arrow)
  var VIEWPORT_MARGIN = 8; // minimum distance kept from the viewport edge
  var SHOW_DELAY = 150;
  var HIDE_DELAY = 80;
  var OPPOSITE = { top: "bottom", bottom: "top", left: "right", right: "left" };

  /**
   * Pure positioning helper (no DOM access) so it can be unit tested.
   * Rects are {top, left, width, height}; viewport is {width, height}.
   * Returns {placement, top, left, arrowOffset} where arrowOffset is the distance of the arrow center from the
   * tooltip's top (left/right placements) or left (top/bottom placements) edge.
   */
  function computePosition(triggerRect, tipRect, viewport, preferred) {
    var fits = function (placement) {
      switch (placement) {
        case "top":
          return triggerRect.top - GAP - tipRect.height >= VIEWPORT_MARGIN;
        case "bottom":
          return triggerRect.top + triggerRect.height + GAP + tipRect.height <= viewport.height - VIEWPORT_MARGIN;
        case "left":
          return triggerRect.left - GAP - tipRect.width >= VIEWPORT_MARGIN;
        default:
          return triggerRect.left + triggerRect.width + GAP + tipRect.width <= viewport.width - VIEWPORT_MARGIN;
      }
    };

    var start = OPPOSITE[preferred] ? preferred : "top";
    var order = [start, OPPOSITE[start]];
    var cross = start === "top" || start === "bottom" ? ["right", "left"] : ["bottom", "top"];
    order = order.concat(cross);

    var placement = order[0];
    for (var i = 0; i < order.length; i++) {
      if (fits(order[i])) {
        placement = order[i];
        break;
      }
    }

    var top;
    var left;
    var arrowOffset;
    var clamp = function (value, size, max) {
      return Math.min(Math.max(value, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, max - size - VIEWPORT_MARGIN));
    };

    if (placement === "top" || placement === "bottom") {
      left = clamp(triggerRect.left + triggerRect.width / 2 - tipRect.width / 2, tipRect.width, viewport.width);
      top = placement === "top" ? triggerRect.top - GAP - tipRect.height : triggerRect.top + triggerRect.height + GAP;
      arrowOffset = triggerRect.left + triggerRect.width / 2 - left;
    } else {
      top = clamp(triggerRect.top + triggerRect.height / 2 - tipRect.height / 2, tipRect.height, viewport.height);
      left = placement === "left" ? triggerRect.left - GAP - tipRect.width : triggerRect.left + triggerRect.width + GAP;
      arrowOffset = triggerRect.top + triggerRect.height / 2 - top;
    }

    return { placement: placement, top: top, left: left, arrowOffset: arrowOffset };
  }

  function init(doc) {
    doc = doc || root.document;
    if (!doc || doc.__kdTooltipReady) return null;
    doc.__kdTooltipReady = true;

    var tip = null;
    var titleEl = null;
    var bodyEl = null;
    var activeTrigger = null;
    var showTimer = null;
    var hideTimer = null;
    var uid = 0;

    function build() {
      tip = doc.createElement("div");
      tip.className = "kd-tooltip";
      tip.id = "kd-tooltip";
      tip.setAttribute("role", "tooltip");
      tip.hidden = true;

      titleEl = doc.createElement("strong");
      titleEl.className = "kd-tooltip__title";
      bodyEl = doc.createElement("span");
      bodyEl.className = "kd-tooltip__body";
      var arrow = doc.createElement("span");
      arrow.className = "kd-tooltip__arrow";
      arrow.setAttribute("aria-hidden", "true");

      tip.appendChild(titleEl);
      tip.appendChild(bodyEl);
      tip.appendChild(arrow);
      doc.body.appendChild(tip);
    }

    function place() {
      var win = doc.defaultView || root;
      var t = activeTrigger.getBoundingClientRect();
      var viewport = { width: doc.documentElement.clientWidth, height: win.innerHeight };
      var size = tip.getBoundingClientRect();
      var result = computePosition(
        { top: t.top, left: t.left, width: t.width, height: t.height },
        { width: size.width, height: size.height },
        viewport,
        activeTrigger.getAttribute("data-kd-tooltip-placement")
      );

      tip.style.top = Math.round(result.top) + "px";
      tip.style.left = Math.round(result.left) + "px";
      tip.style.setProperty("--kd-tooltip-arrow-offset", Math.round(result.arrowOffset) + "px");
      tip.setAttribute("data-placement", result.placement);
    }

    function show(trigger) {
      var text = trigger.getAttribute("data-kd-tooltip");
      if (!text) return;
      if (!tip) build();

      if (activeTrigger && activeTrigger !== trigger) activeTrigger.removeAttribute("aria-describedby");

      var title = trigger.getAttribute("data-kd-tooltip-title");
      titleEl.textContent = title || "";
      titleEl.hidden = !title;
      bodyEl.textContent = text;

      activeTrigger = trigger;
      trigger.setAttribute("aria-describedby", tip.id);

      // Position while invisible (display needs to be on for measuring), then fade in.
      tip.hidden = false;
      tip.classList.remove("is-visible");
      place();
      // Force a reflow so the transition runs from the hidden state.
      void tip.offsetWidth;
      tip.classList.add("is-visible");
    }

    function hide() {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      if (!activeTrigger) return;
      activeTrigger.removeAttribute("aria-describedby");
      activeTrigger = null;
      tip.classList.remove("is-visible");
      tip.hidden = true;
    }

    function scheduleShow(trigger, delay) {
      clearTimeout(hideTimer);
      clearTimeout(showTimer);
      if (activeTrigger === trigger) return;
      showTimer = setTimeout(function () {
        show(trigger);
      }, delay);
    }

    function scheduleHide() {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(hide, HIDE_DELAY);
    }

    function triggerFrom(event) {
      var target = event.target;
      return target && target.closest ? target.closest(SELECTOR) : null;
    }

    function isHoverPointer(event) {
      return event.pointerType === "mouse" || event.pointerType === "pen";
    }

    doc.addEventListener("pointerover", function (event) {
      var trigger = triggerFrom(event);
      if (trigger && isHoverPointer(event)) scheduleShow(trigger, SHOW_DELAY);
    });

    doc.addEventListener("pointerout", function (event) {
      var trigger = triggerFrom(event);
      if (!trigger || !isHoverPointer(event)) return;
      // Ignore moves between children of the same trigger.
      if (event.relatedTarget && trigger.contains(event.relatedTarget)) return;
      scheduleHide();
    });

    doc.addEventListener("focusin", function (event) {
      var trigger = triggerFrom(event);
      if (!trigger) return;
      // Only keyboard focus: a mouse click focuses the element too and would re-open the tooltip we just closed.
      var keyboardFocus = true;
      try {
        keyboardFocus = trigger.matches(":focus-visible");
      } catch (e) {
        /* :focus-visible unsupported — fall back to showing */
      }
      if (keyboardFocus) scheduleShow(trigger, 0);
    });

    doc.addEventListener("focusout", function (event) {
      if (triggerFrom(event)) scheduleHide();
    });

    doc.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && activeTrigger) hide();
    });

    // Clicking the trigger performs its action, so the explanation is no longer needed.
    doc.addEventListener("click", function (event) {
      if (triggerFrom(event)) hide();
    });

    var win = doc.defaultView || root;
    win.addEventListener("scroll", hide, { passive: true, capture: true });
    win.addEventListener("resize", hide);

    return { show: show, hide: hide };
  }

  var api = { init: init, computePosition: computePosition };

  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  } else {
    root.KDTooltip = api;
    if (root.document) {
      if (root.document.readyState === "loading") {
        root.document.addEventListener("DOMContentLoaded", function () {
          init(root.document);
        });
      } else {
        init(root.document);
      }
    }
  }
})(typeof window !== "undefined" ? window : this);
